import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, AlertTriangle, Ban, Pencil, CheckCircle2, Clock, CloudOff, RefreshCw, Trash2, Lock, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSync } from "@/context/SyncContext";
import { AppHeader } from "@/components/AppHeader";
import { TimePicker } from "@/components/TimePicker";
import { DatePicker } from "@/components/DatePicker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { SERVICES, fmtDuration, fmtDateIt, slugFromKey, entityKindOf, CASA_RIPOSO_ROLE_ASA } from "@/lib/services";
import { fmtNum } from "@/lib/format";
import { StampInfo } from "@/components/StampInfo";
import { GeoAnomalyNote } from "@/components/GeoAnomalyNote";
import { postWithGeo, GEO_REQUIRED_TITLE } from "@/lib/geo";
import { WorkerRelationsSection } from "@/components/worker/WorkerRelationsSection";
import { AttendanceDayRegister } from "@/components/worker/AttendanceDayRegister";

const ANOMALY_REASONS = ["Emergenza di servizio", "Problema tecnico", "Dimenticanza", "Altro"];

// ID evento univoco con fallback: crypto.randomUUID non è disponibile su WebView Android datati
// (es. Xiaomi con Android System WebView < 92) → senza fallback l'apertura/chiusura servizio
// lanciava un'eccezione. Il fallback garantisce il funzionamento su tutti i dispositivi.
const eventId = () =>
  (typeof window !== "undefined" && window.crypto && typeof window.crypto.randomUUID === "function")
    ? window.crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const DECLARATION =
  "Dichiaro che la prestazione lavorativa indicata è stata effettivamente svolta e che i dati inseriti sono, per quanto a mia conoscenza, veritieri e completi.";

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

// Orario/data ancorati al fuso Europe/Rome, indipendenti dal fuso del dispositivo,
// così l'ora dichiarata di default coincide con l'ora legale italiana usata dal server per il controllo anomalia.
function romeParts() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { year: p.year, month: p.month, day: p.day, hour: parseInt(p.hour, 10) % 24, minute: parseInt(p.minute, 10) };
}

function todayStr() {
  const p = romeParts();
  return `${p.year}-${p.month}-${p.day}`;
}

function roundNow() {
  const p = romeParts();
  let total = Math.round((p.hour * 60 + p.minute) / 5) * 5;
  if (total > 23 * 60 + 55) total = 23 * 60 + 55;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export default function RegisterView() {
  const { slug, entityId } = useParams();
  const [params] = useSearchParams();
  const qYear = params.get("year");
  const qMonth = params.get("month");
  const qKind = params.get("kind") || "";
  const qRole = params.get("role") || "";
  const qOpen = params.get("open") === "1";
  const qStart = params.get("start") || "";
  const qDate = params.get("date") || "";
  const qAtt = params.get("att") || "";
  const qAnomalyEntry = params.get("anomaly_entry") || "";
  const qAnomalyPhase = params.get("anomaly_phase") || "";
  const navigate = useNavigate();
  const cfg = SERVICES[slug];
  const { draftsFor, removeDraft, syncAll } = useSync();
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [relCount, setRelCount] = useState(0);
  const [relOpen, setRelOpen] = useState(0);
  const [tab, setTab] = useState(params.get("tab") === "relazioni" ? "relazioni" : "registro");
  const [openOpen, setOpenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [anomalyTarget, setAnomalyTarget] = useState(null);
  const [correctTarget, setCorrectTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingOpen, setDeletingOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const [attReg, setAttReg] = useState(null);
  const attOpenedRef = useRef(false);
  const anomalyOpenedRef = useRef(false);
  useEffect(() => {
    if (qAtt && reg?.attendance_context && !attOpenedRef.current) {
      attOpenedRef.current = true;
      setAttReg({ date: qAtt });
    }
  }, [qAtt, reg]);

  // Anomalia timbratura post-apertura: quando l'apertura genera un errore di timbratura su
  // un'entità diversa (es. sede operativa ASA/OSS), atterriamo sulla pagina corretta e riapriamo
  // qui il dialog "motivazione" (obbligatorio), evitando la schermata intermedia sull'appalto.
  useEffect(() => {
    if (qAnomalyEntry && !anomalyOpenedRef.current) {
      anomalyOpenedRef.current = true;
      setAnomalyTarget({ entryId: qAnomalyEntry, phase: qAnomalyPhase || "open" });
    }
  }, [qAnomalyEntry, qAnomalyPhase]);

  const loadActive = useCallback(async () => {
    try { const { data } = await api.get("/service/active"); setActive(data.active); }
    catch { /* offline */ }
  }, []);

  const load = useCallback(async () => {
    let url = `/register?service_type=${cfg.key}&entity_id=${entityId}`;
    if (qYear && qMonth) url += `&year=${qYear}&month=${qMonth}`;
    if (qKind) url += `&entity_kind=${qKind}`;
    try {
      const { data } = await api.get(url);
      setReg(data);
    } catch {
      // offline o errore di rete: mantieni la vista corrente
    } finally {
      setLoading(false);
    }
  }, [cfg, entityId, qYear, qMonth, qKind]);

  useEffect(() => { load(); loadActive(); }, [load, loadActive]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/worker/relations/by-user?user_id=${entityId}`)
      .then((r) => { if (!cancelled) { const d = r.data || []; setRelCount(d.length); setRelOpen(d.filter((x) => x.can_edit).length); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [entityId]);

  // Auto-apertura del dialog "Apri servizio" quando arrivi dal pulsante suggerito (open=1),
  // solo se il mese è aperto e non c'è già una timbratura in corso.
  useEffect(() => {
    if (qOpen && !autoOpened && !loading && reg?.month_open && !qYear && !active) {
      setOpenOpen(true);
      setAutoOpened(true);
    }
  }, [qOpen, autoOpened, loading, reg, qYear, active]);

  const deleteOpenStamp = useCallback(async () => {
    setDeletingOpen(true);
    try {
      await api.delete("/service/open");
      toast.success("Timbratura aperta eliminata", { duration: 2200, icon: <Trash2 className="h-4 w-4" /> });
      setConfirmDeleteOpen(false);
      setActive(null);
      await loadActive();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile eliminare la timbratura");
    } finally { setDeletingOpen(false); }
  }, [loadActive, load]);

  if (!cfg) return null;
  const Icon = cfg.icon;
  const drafts = draftsFor(cfg.key, entityId);

  // Servizi role-gated (Nido): il ruolo è già stato scelto nel picker e determina l'entità.
  // Generalizzato: se il ruolo arriva nell'URL (es. ripristino di un Registro frequente Casa di riposo)
  // viene bloccato a quel valore; altrimenti fallback storico Nido.
  const forcedRole = qRole || (cfg.roleEntity ? (qKind === "appalto" ? "Ausiliario" : "Educatore") : null);
  // Servizi standalone con picker auto-collassato (entity_kind "appalto", autoSelectSingle):
  // il picker si salta con un solo appalto, quindi "Indietro" deve tornare alla Home Lavoratore
  // (altrimenti rimbalzerebbe sul picker che riapre lo stesso registro). I servizi role-gated
  // (Nido) restano invariati: tornano al picker per la scelta del ruolo.
  const autoSingleBack = !cfg.roleEntity && entityKindOf(cfg).autoSelectSingle;
  const backTarget = qYear ? "/archivio" : (autoSingleBack ? "/lavoratore" : `/servizio/${slug}`);
  const attCtx = reg?.attendance_context ? { ...reg.attendance_context, service: cfg.key } : null;

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-24">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => navigate(backTarget)} data-testid="register-back"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
        </button>

        {loading || !reg ? (
          <p className="text-sm text-slate-500">Caricamento…</p>
        ) : (
          <>
            {/* Entity header */}
            <Card className="rounded-2xl border-slate-200 shadow-sm mb-4">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner shrink-0">
                    <Icon className="h-5 w-5 text-[#22364D]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/70">{cfg.short}</p>
                    <h1 className="font-heading text-xl font-bold text-slate-900 leading-tight" data-testid="register-entity-title">
                      {reg.entity?.titolo}
                    </h1>
                    <p className="text-sm text-slate-500">{reg.entity?.sottotitolo}</p>
                    {reg.entity?.monte_ore_settimanale != null && (
                      <p className="text-sm text-slate-500 mt-1">Monte ore settimanale: <span className="font-semibold text-slate-700">{fmtNum(reg.entity?.monte_ore_settimanale)}h</span></p>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{MONTHS[reg.month - 1]} {reg.year}</p>
                    <p className="text-sm text-slate-500">Totale tue ore</p>
                  </div>
                  <p className="font-heading text-2xl font-bold text-primary" data-testid="register-total">{fmtDuration(reg.total_minutes)}</p>
                </div>
                {!reg.month_open && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Mese archiviato: registrazioni in sola lettura.
                  </div>
                )}
              </CardContent>
            </Card>

            {relCount > 0 && (
              <div className="flex gap-2 mb-4" data-testid="register-tabs">
                <button onClick={() => setTab("registro")} data-testid="reg-tab-registro-btn"
                  className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${tab === "registro" ? "bg-[#22364D] text-white border-[#22364D]" : "bg-white text-slate-600 border-slate-200 hover:border-[#93B4CC]"}`}>Registro</button>
                <button onClick={() => setTab("relazioni")} data-testid="reg-tab-relazioni-btn"
                  className={`px-4 py-1.5 text-sm rounded-full border transition-colors inline-flex items-center gap-1.5 ${tab === "relazioni" ? "bg-[#22364D] text-white border-[#22364D]" : "bg-white text-slate-600 border-slate-200 hover:border-[#93B4CC]"}`}>
                  Relazioni {relOpen > 0 && <span data-testid="reg-tab-relazioni-badge" className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold ${tab === "relazioni" ? "bg-white/20 text-white" : "bg-amber-500 text-white"}`}>{relOpen}</span>}
                </button>
              </div>
            )}

            <div className={tab === "relazioni" ? "hidden" : ""}>

            {reg.month_open && !qYear && (
              active && active.entity?.id === entityId ? (
                <div className="mb-5 space-y-2">
                  {active.role_or_task && (
                    <div className="flex justify-between gap-3 text-xs rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2">
                      <span className="text-slate-500">Ruolo</span>
                      <span className="font-medium text-slate-800" data-testid="active-role">{active.role_or_task}</span>
                    </div>
                  )}
                  <StampInfo entry={active} data-testid="active-stamp-info" />
                  {attCtx && (
                    <Button type="button" variant="outline" data-testid="attendance-open-active"
                      onClick={() => setAttReg({ date: active.work_date })}
                      className="w-full rounded-lg h-11 font-semibold border-primary/30 text-primary hover:bg-primary/5">
                      <Users2 className="h-4 w-4 mr-2" /> Presenze utenti{attCtx.label ? ` · ${attCtx.label}` : ""}
                    </Button>
                  )}
                  <Button data-testid="close-service-btn" onClick={() => setCloseOpen(true)}
                    className="w-full rounded-lg h-12 font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-all active:scale-[0.98]">
                    <Clock className="h-5 w-5 mr-2" /> Servizio in corso · Chiudi servizio
                  </Button>
                  <Button data-testid="delete-open-service-btn" variant="outline" onClick={() => setConfirmDeleteOpen(true)}
                    className="w-full rounded-lg h-11 font-semibold text-destructive border-destructive/30 hover:bg-destructive/5 transition-all active:scale-[0.98]">
                    <Trash2 className="h-4 w-4 mr-2" /> Elimina timbratura
                  </Button>
                </div>
              ) : active ? (
                <div data-testid="service-locked-banner" className="mb-5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  Hai un servizio in corso su <b>{active.entity?.titolo || "un altro servizio"}</b>. Chiudilo prima di aprirne un altro.
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Button variant="link" className="px-1 h-auto text-amber-900 underline" data-testid="goto-active-service"
                      onClick={() => navigate(`/registro/${slugFromKey(active.service_type)}/${active.entity?.id}`)}>Vai al servizio</Button>
                    <Button variant="link" className="px-1 h-auto text-destructive underline" data-testid="delete-open-service-banner-btn"
                      onClick={() => setConfirmDeleteOpen(true)}>Elimina timbratura</Button>
                  </div>
                </div>
              ) : (
                <Button data-testid="open-service-btn" onClick={() => setOpenOpen(true)}
                  className="w-full rounded-lg h-12 font-semibold mb-5 transition-all duration-200 hover:brightness-110 active:scale-[0.98]">
                  <Plus className="h-5 w-5 mr-2" /> Apri servizio
                </Button>
              )
            )}

            {/* Bozze offline */}
            {drafts.length > 0 && (
              <div className="space-y-3 mb-3" data-testid="drafts-list">
                {drafts.map((d) => (
                  <Card key={d.id} data-testid={`draft-${d.id}`}
                    className={`rounded-xl shadow-sm border ${d.status === "conflict" || d.status === "archived" ? "border-destructive/30 bg-destructive/5" : "border-amber-300 bg-amber-50"}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <CloudOff className={`h-4 w-4 ${d.status === "pending" ? "text-amber-600" : "text-destructive"}`} />
                        <p className="font-semibold text-slate-800 capitalize">{fmtDateIt(d.payload.work_date)}</p>
                        <Badge className={`border-0 text-[10px] ${d.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-destructive/10 text-destructive"}`}>
                          {d.status === "pending" ? "Da sincronizzare" : d.status === "archived" ? "Mese archiviato" : "Errore di sincronizzazione"}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{d.payload.start_time}–{d.payload.end_time}{d.payload.role_or_task && ` · ${d.payload.role_or_task}`}</p>
                      {d.error && <p className="text-xs text-destructive mt-1">{d.error}</p>}
                      <div className="flex gap-2 mt-3">
                        {d.status !== "archived" && (
                          <Button variant="outline" size="sm" onClick={() => syncAll()} data-testid={`retry-draft-${d.id}`} className="rounded-lg text-primary border-primary/30">
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Riprova
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => removeDraft(d.id)} data-testid={`delete-draft-${d.id}`} className="rounded-lg text-destructive border-destructive/30">
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Elimina bozza
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Entries */}
            {(reg.entries?.length ?? 0) === 0 ? (
              <Card className="rounded-xl border-slate-200 shadow-sm">
                <CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="register-empty">
                  Nessuna registrazione questo mese.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="entries-list">
                {(reg.entries || []).map((e, i) => {
                  const cancelled = e.status === "annullata";
                  return (
                    <motion.div key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.3) }}>
                      <Card className={`rounded-xl border-slate-200 shadow-sm ${cancelled ? "opacity-70" : ""}`} data-testid={`entry-${e.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className={`font-semibold text-slate-800 capitalize ${cancelled ? "line-through" : ""}`}>{fmtDateIt(e.work_date)}</p>
                                {e.corretta && !cancelled && (
                                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[10px]" data-testid={`entry-corrected-${e.id}`}>
                                    <AlertTriangle className="h-3 w-3 mr-1" /> Corretta
                                  </Badge>
                                )}
                                {cancelled && (
                                  <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10 border-0 text-[10px]" data-testid={`entry-cancelled-${e.id}`}>
                                    Annullata
                                  </Badge>
                                )}
                              </div>
                              <p className={`text-sm text-slate-500 ${cancelled ? "line-through" : ""}`}>
                                {e.start_time}–{e.end_time}
                                {e.role_or_task && ` · ${e.role_or_task}`}
                              </p>
                            </div>
                            <p className={`font-heading font-bold text-primary shrink-0 ${cancelled ? "line-through" : ""}`}>
                              {fmtDuration(e.duration_minutes)}
                            </p>
                          </div>
                          <StampInfo entry={e} className="mt-3" />
                          {attCtx && !cancelled && (
                            <Button type="button" variant="outline" size="sm" data-testid={`attendance-open-${e.id}`}
                              onClick={() => setAttReg({ date: e.work_date })}
                              className="w-full mt-3 rounded-lg border-primary/30 text-primary hover:bg-primary/5">
                              <Users2 className="h-3.5 w-3.5 mr-1.5" /> Presenze utenti
                            </Button>
                          )}
                          {!cancelled && reg.month_open && (
                            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                              <Button variant="outline" size="sm" data-testid={`correct-btn-${e.id}`}
                                onClick={() => setCorrectTarget(e)} className="rounded-lg text-slate-600">
                                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Correggi
                              </Button>
                              <Button variant="outline" size="sm" data-testid={`cancel-btn-${e.id}`}
                                onClick={() => setCancelTarget(e)}
                                className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/5">
                                <Ban className="h-3.5 w-3.5 mr-1.5" /> Annulla
                              </Button>
                            </div>
                          )}
                          {cancelled && e.cancelled_reason && (
                            <p className="text-xs text-slate-400 mt-2 italic">Motivo: {e.cancelled_reason}</p>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
            </div>
          </>
        )}
        {tab === "relazioni" && <WorkerRelationsSection userId={entityId} />}
      </main>

      {openOpen && <OpenServiceDialog cfg={cfg} entityId={entityId} entityKind={qKind} forcedRole={forcedRole} initialDate={qDate} initialStart={qStart} onClose={() => setOpenOpen(false)}
        onOpened={(res, opened) => {
          setOpenOpen(false);
          const goesToOtherEntity = opened?.entityId && opened.entityId !== entityId;
          if (res?.needs_reason) {
            // Anomalia: se l'apertura è avvenuta su un'entità diversa (sede operativa), naviga lì
            // portando il contesto anomalia così da riaprire il dialog "motivazione" sulla pagina giusta.
            if (goesToOtherEntity) {
              const params = new URLSearchParams();
              if (opened.entityKind) params.set("kind", opened.entityKind);
              if (opened.role) params.set("role", opened.role);
              params.set("anomaly_entry", res.entry.id);
              params.set("anomaly_phase", "open");
              navigate(`/registro/${slugFromKey(cfg.key)}/${opened.entityId}?${params.toString()}`);
              return;
            }
            loadActive(); load(); setAnomalyTarget({ entryId: res.entry.id, phase: "open", geo: res.entry.open_geo?.out_of_area ? res.entry.open_geo : null }); return;
          }
          // Apertura riuscita: vai direttamente al servizio in corso (stessa destinazione di "Vai al servizio"),
          // evitando la schermata intermedia quando l'entità aperta (es. sede operativa) differisce dall'URL corrente.
          if (goesToOtherEntity) {
            const params = new URLSearchParams();
            if (opened.entityKind) params.set("kind", opened.entityKind);
            if (opened.role) params.set("role", opened.role);
            const qs = params.toString();
            navigate(`/registro/${slugFromKey(cfg.key)}/${opened.entityId}${qs ? `?${qs}` : ""}`);
            return;
          }
          loadActive(); load();
        }} />}
      {closeOpen && <CloseServiceDialog startDate={active?.work_date} onClose={() => setCloseOpen(false)}
        onClosed={(res) => { setCloseOpen(false); loadActive(); load(); if (res?.needs_reason) setAnomalyTarget({ entryId: res.entry.id, phase: "close", geo: res.entry.close_geo?.out_of_area ? res.entry.close_geo : null }); }} />}
      {anomalyTarget && <AnomalyReasonDialog target={anomalyTarget} onClose={() => setAnomalyTarget(null)} onSaved={() => { setAnomalyTarget(null); load(); }} />}
      {correctTarget && <CorrectDialog cfg={cfg} entry={correctTarget} onClose={() => setCorrectTarget(null)} onSaved={() => { setCorrectTarget(null); load(); }} />}
      {cancelTarget && <CancelDialog entry={cancelTarget} onClose={() => setCancelTarget(null)} onSaved={() => { setCancelTarget(null); load(); }} />}
      {attCtx && attReg && <AttendanceDayRegister open onClose={() => setAttReg(null)} ctx={attCtx} date={attReg.date} serviceLabel={cfg.short} />}

      <Dialog open={confirmDeleteOpen} onOpenChange={(o) => !o && setConfirmDeleteOpen(false)}>
        <DialogContent className="rounded-2xl" data-testid="delete-open-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Eliminare questa timbratura?</DialogTitle>
            <DialogDescription>L'apertura verrà eliminata e non verrà registrata alcuna prestazione.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} data-testid="delete-open-cancel-btn"
              className="w-full sm:w-auto rounded-lg">Annulla</Button>
            <Button onClick={deleteOpenStamp} disabled={deletingOpen} data-testid="delete-open-confirm-btn"
              className="w-full sm:w-auto rounded-lg bg-destructive hover:bg-destructive/90 text-white">
              <Trash2 className="h-4 w-4 mr-2" /> {deletingOpen ? "Eliminazione…" : "Elimina timbratura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Apertura servizio (timbratura entrata) ---------- */
function OpenServiceDialog({ cfg, entityId, entityKind, forcedRole, initialDate, initialStart, onClose, onOpened }) {
  const [date, setDate] = useState(initialDate || todayStr());
  const [start, setStart] = useState(initialStart || roundNow());
  const [role, setRole] = useState(forcedRole || "");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Casa di riposo + ASA / OSS: sede operativa condizionale (riusa le Sedi operative Orbit).
  const isCasa = cfg.key === "casa_di_riposo";
  const casaAsaNeedsSite = isCasa && role === CASA_RIPOSO_ROLE_ASA && entityKind !== "operative_site";
  const [sites, setSites] = useState(null);
  const [siteId, setSiteId] = useState("");
  const [geoError, setGeoError] = useState("");

  useEffect(() => {
    if (!casaAsaNeedsSite) { setSites(null); setSiteId(""); return; }
    let cancelled = false;
    api.get(`/operative-sites?service=casa_di_riposo`)
      .then((r) => { if (!cancelled) setSites((r.data || []).filter((d) => d.appalto_id === entityId)); })
      .catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [casaAsaNeedsSite, entityId]);

  useEffect(() => {
    if (!cfg.roles || forcedRole) return;
    let cancelled = false;
    api.get(`/service/suggested-role?service_type=${cfg.key}&entity_id=${entityId}`)
      .then((r) => { if (!cancelled && r.data?.role) setRole(r.data.role); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cfg.roles, cfg.key, entityId, forcedRole]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setGeoError("");
    if (cfg.roles && !role) return setError("Seleziona il ruolo/mansione");
    let postEntityId = entityId;
    let postKind = entityKind || null;
    if (casaAsaNeedsSite && sites && sites.length > 0) {
      if (!siteId) return setError("Seleziona la sede operativa");
      postEntityId = siteId; postKind = "operative_site";
    }
    if (!date) return setError("Seleziona la data di inizio");
    if (!start || start.split(":").length < 2 || start.endsWith(":") || start.startsWith(":")) return setError("Seleziona ora e minuti di inizio");
    if (!confirmed) return setError("Devi confermare la dichiarazione di entrata in servizio");
    setSaving(true);
    try {
      const { data } = await postWithGeo(api, "/service/open", {
        service_type: cfg.key, entity_id: postEntityId, work_date: date, start_time: start,
        role_or_task: cfg.roles ? role : null, entity_kind: postKind, declaration_confirmed: true,
        client_open_at: new Date().toISOString(), open_event_id: eventId(),
      });
      toast.success(data.anomaly ? "Servizio aperto · anomalia rilevata" : "Servizio aperto", { duration: 2200, icon: <CheckCircle2 className="h-4 w-4" /> });
      onOpened(data, { entityId: postEntityId, entityKind: postKind, role: cfg.roles ? role : null });
    } catch (err) {
      if (err.geo) setGeoError(err.message);
      else setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Apri servizio</DialogTitle>
          <DialogDescription>Timbra l'entrata in servizio. Verrà registrato l'orario dichiarato e quello del server.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="open-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          {geoError && <GeoRequiredBox message={geoError} testid="open-geo-error" />}
          <DatePicker label="Data di inizio" value={date} onChange={setDate} testid="open-date" />
          <TimePicker label="Ora di inizio" value={start} onChange={setStart} testid="open-start" />
          {cfg.roles && (
            forcedRole ? (
              <div data-testid="open-role-locked" className="flex items-center gap-2 text-sm px-1">
                <span className="text-slate-500">{cfg.key === "centro_estivo" ? "Mansione:" : "Ruolo:"}</span>
                <span className="font-semibold text-slate-800">{forcedRole}</span>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{cfg.key === "centro_estivo" ? "Mansione" : "Ruolo"}</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="open-role-select" className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{cfg.roles.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            )
          )}
          {casaAsaNeedsSite && (
            sites === null ? (
              <p className="text-sm text-slate-500">Caricamento sedi operative…</p>
            ) : sites.length > 0 ? (
              <div className="space-y-1.5">
                <Label>Sede operativa</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger data-testid="open-site-select" className="h-11"><SelectValue placeholder="Seleziona la sede…" /></SelectTrigger>
                  <SelectContent>{sites.map((s) => (<SelectItem key={s.id} value={s.id}>{s.denominazione}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-xs text-slate-500" data-testid="open-site-none">Nessuna sede operativa configurata: la prestazione sarà registrata sul contesto generale.</p>
            )
          )}
          <label className="flex items-start gap-3 rounded-lg bg-primary/5 border border-primary/20 p-3 cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="open-declaration-checkbox" className="mt-0.5" />
            <span className="text-sm font-medium text-slate-700 leading-relaxed">Dichiari di entrare in servizio all'orario indicato?</span>
          </label>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="submit-open-btn" className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Timbratura…" : geoError ? "Riprova" : "Timbra entrata"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Blocco timbratura: posizione non disponibile/non affidabile (nessuna timbratura registrata) ---------- */
function GeoRequiredBox({ message, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">
      <p className="font-semibold">{GEO_REQUIRED_TITLE}</p>
      <p className="mt-0.5">{message}</p>
    </div>
  );
}

/* ---------- Chiusura servizio (timbratura uscita) ---------- */
function CloseServiceDialog({ startDate, onClose, onClosed }) {
  const [endDate, setEndDate] = useState(startDate || todayStr());
  const [end, setEnd] = useState(roundNow());
  const [declared, setDeclared] = useState(false);
  const [error, setError] = useState("");
  const [geoError, setGeoError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setGeoError("");
    if (!endDate) return setError("Seleziona la data di fine");
    if (!end || end.split(":").length < 2 || end.endsWith(":") || end.startsWith(":")) return setError("Seleziona ora e minuti di fine");
    if (!declared) return setError("Devi confermare la dichiarazione");
    setSaving(true);
    try {
      const { data } = await postWithGeo(api, "/service/close", {
        end_time: end, end_date: endDate, declaration_confirmed: true,
        client_close_at: new Date().toISOString(), close_event_id: eventId(),
      });
      toast.success(data.anomaly ? "Servizio chiuso · anomalia rilevata" : "Servizio chiuso", { duration: 2200, icon: <CheckCircle2 className="h-4 w-4" /> });
      onClosed(data);
    } catch (err) {
      if (err.geo) setGeoError(err.message);
      else setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Chiudi servizio</DialogTitle>
          <DialogDescription>Timbra l'uscita. Se il turno prosegue oltre la mezzanotte, imposta la data di fine al giorno successivo.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="close-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          {geoError && <GeoRequiredBox message={geoError} testid="close-geo-error" />}
          <DatePicker label="Data di fine" value={endDate} onChange={setEndDate} testid="close-date" />
          <TimePicker label="Ora di fine" value={end} onChange={setEnd} testid="close-end" />
          <label className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-100 p-3 cursor-pointer">
            <Checkbox checked={declared} onCheckedChange={(v) => setDeclared(!!v)} data-testid="close-declaration-checkbox" className="mt-0.5" />
            <span className="text-xs text-slate-600 leading-relaxed">{DECLARATION}</span>
          </label>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="submit-close-btn" className="w-full rounded-lg h-11 font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? "Timbratura…" : geoError ? "Riprova" : "Timbra uscita"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Motivazione anomalia di timbratura ---------- */
function AnomalyReasonDialog({ target, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason) return setError("Seleziona la motivazione");
    if (reason === "Altro" && !note.trim()) return setError("Specifica la motivazione nel campo di testo");
    setSaving(true);
    try {
      await api.patch(`/service/${target.entryId}/anomaly-reason`, { phase: target.phase, reason, note });
      toast.success("Motivazione registrata", { duration: 2000 });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => { /* obbligatoria: non chiudibile senza motivazione */ }}>
      <DialogContent className="rounded-2xl" data-testid="anomaly-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Errore di timbratura</DialogTitle>
          <DialogDescription>È stato rilevato un errore di timbratura. Seleziona la motivazione. L'Ufficio provvederà alla verifica.</DialogDescription>
        </DialogHeader>
        <GeoAnomalyNote phase={target.phase} geo={target.geo} testid="anomaly-geo-detail" className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" />
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="anomaly-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="space-y-1.5">
            <Label>Motivazione</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="anomaly-reason-select" className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>{ANOMALY_REASONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          {reason === "Altro" && (
            <div className="space-y-1.5">
              <Label htmlFor="anote">Specifica</Label>
              <Textarea id="anote" value={note} data-testid="anomaly-note-input" onChange={(e) => setNote(e.target.value)} placeholder="Descrivi la motivazione" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="submit-anomaly-btn" className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Invio…" : "Invia motivazione"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Correzione ---------- */
function CorrectDialog({ cfg, entry, onClose, onSaved }) {
  const [workDate, setWorkDate] = useState(entry.work_date);
  const [endDate, setEndDate] = useState(entry.end_date || entry.work_date);
  const [start, setStart] = useState(entry.start_time);
  const [end, setEnd] = useState(entry.end_time);
  const [role, setRole] = useState(entry.role_or_task || "");
  const [reason, setReason] = useState("");
  const [anomalyReason, setAnomalyReason] = useState("");
  const [anomalyNote, setAnomalyNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const dtChanged =
    workDate !== entry.work_date || start !== entry.start_time ||
    endDate !== (entry.end_date || entry.work_date) || end !== entry.end_time;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (cfg.roles && !role) return setError("Seleziona il ruolo/mansione");
    if (dtChanged) {
      if (!anomalyReason) return setError("Seleziona la motivazione della modifica");
      if (anomalyReason === "Altro" && !anomalyNote.trim()) return setError("Con 'Altro' la nota è obbligatoria");
    } else if (!reason.trim()) {
      return setError("La motivazione è obbligatoria");
    }
    setSaving(true);
    try {
      const readable = dtChanged
        ? (anomalyReason === "Altro" ? `Altro: ${anomalyNote.trim()}` : anomalyReason)
        : reason.trim();
      await api.post(`/work-entries/${entry.id}/correct`, {
        work_date: workDate, start_time: start, end_date: endDate, end_time: end,
        role_or_task: cfg.roles ? role : null, reason: readable,
        anomaly_reason: dtChanged ? anomalyReason : null,
        anomaly_note: dtChanged ? anomalyNote.trim() : "",
      });
      toast.success(dtChanged ? "Modifica registrata come anomalia da verificare" : "Registrazione corretta", { duration: 2400 });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Correggi prestazione</DialogTitle>
          <DialogDescription>La versione originale viene conservata nella cronologia.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div data-testid="correct-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>
          )}
          {dtChanged && (
            <div data-testid="correct-anomaly-notice" className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>La modifica di una timbratura già confermata sarà registrata come anomalia e dovrà essere verificata dall'Ufficio.</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <DatePicker label="Data inizio" value={workDate} onChange={setWorkDate} testid="correct-start-date" />
            <DatePicker label="Data fine" value={endDate} onChange={setEndDate} testid="correct-end-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TimePicker label="Inizio" value={start} onChange={setStart} testid="correct-start" />
            <TimePicker label="Fine" value={end} onChange={setEnd} testid="correct-end" />
          </div>
          {cfg.roles && (
            cfg.roleEntity ? (
              <div className="space-y-1.5">
                <Label>Ruolo</Label>
                <div data-testid="correct-role-locked"
                  className="h-11 flex items-center rounded-lg bg-slate-50 border border-slate-200 px-3 text-sm font-medium text-slate-700">
                  {role || "—"}
                </div>
                <p className="text-xs text-slate-400">Per cambiare ruolo o sala, annulla e apri una nuova prestazione.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{cfg.key === "centro_estivo" ? "Mansione" : "Ruolo"}</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="correct-role-select" className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{cfg.roles.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            )
          )}
          {dtChanged ? (
            <>
              <div className="space-y-1.5">
                <Label>Motivazione della modifica</Label>
                <Select value={anomalyReason} onValueChange={setAnomalyReason}>
                  <SelectTrigger data-testid="correct-anomaly-reason-select" className="h-11"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>{ANOMALY_REASONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              {anomalyReason === "Altro" && (
                <div className="space-y-1.5">
                  <Label htmlFor="canote">Specifica</Label>
                  <Textarea id="canote" value={anomalyNote} data-testid="correct-anomaly-note-input"
                    onChange={(e) => setAnomalyNote(e.target.value)} placeholder="Descrivi la motivazione" />
                </div>
              )}
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="reason">Motivazione</Label>
              <Textarea id="reason" required value={reason} data-testid="correct-reason-input"
                onChange={(e) => setReason(e.target.value)} placeholder="Indica il motivo della correzione" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="submit-correct-btn" className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Salvataggio…" : "Salva correzione"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Annullamento ---------- */
function CancelDialog({ entry, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason.trim()) return setError("La motivazione è obbligatoria");
    setSaving(true);
    try {
      await api.post(`/work-entries/${entry.id}/cancel`, { reason });
      toast.success("Registrazione annullata", { duration: 2000 });
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Annulla prestazione</DialogTitle>
          <DialogDescription>La registrazione resta nello storico ma non sarà conteggiata nei totali.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div data-testid="cancel-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="creason">Motivazione</Label>
            <Textarea id="creason" required value={reason} data-testid="cancel-reason-input"
              onChange={(e) => setReason(e.target.value)} placeholder="Indica il motivo dell'annullamento" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="submit-cancel-btn"
              className="w-full rounded-lg h-11 font-semibold bg-destructive hover:bg-destructive/90">
              {saving ? "Annullamento…" : "Conferma annullamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
